:host {
  --stzh-flyingfocus-color: #{$colorWhite};

  --background-color: var(--stzh-cta-background-color, #{$ctaBackgroundColor});
  --heading-color: #{$colorWhite};
  --lead-color: #{$colorWhite};
  --sticky-background-color: var(--background-color);
  --button-color: var(--stzh-cta-button-color, #{$colorWhite});
  --button-background-color: var(
    --stzh-cta-button-background-color,
    #{$colorPrimary70}
  );
  --button-border-radius: #{$buttonBorderRadius};

  --hover-button-color: var(--stzh-cta-hover-button-color, #{$colorWhite});
  --hover-button-background-color: var(
    --stzh-cta-hover-button-background-color,
    #{$colorSecondary60}
  );

  --stuck-background-color: #{$colorGrey10};
  --stuck-heading-color: #{$colorPrimary70};
  --stuck-lead-color: #{$baseColor};
  --stuck-sticky-background-color: var(--background-color);

  --grid-template-areas: var(--stzh-cta-grid-template-areas, "cta cta cta cta");

  --grid-template-columns: var(
    --stzh-cta-grid-template-columns,
    #{$gridColumns}
  );

  @media print {
    display: none;
  }

  @include mq($from: medium) {
    --grid-template-areas: var(
      --stzh-cta-grid-template-areas,
      "cta cta cta cta cta cta cta cta"
    );

    --grid-template-columns: var(
      --stzh-cta-grid-template-columns,
      #{$gridColumnsMedium}
    );
  }

  @include mq($from: large) {
    --grid-template-areas: var(
      --stzh-cta-grid-template-areas,
      "cta cta cta cta cta cta cta cta"
    );

    --grid-template-columns: var(
      --stzh-cta-grid-template-columns,
      repeat(8, minmax(0, 1fr))
    );
  }

  &[size="small"] {
    --button-background-color: transparent;
    --button-color: var(--stzh-color-white);
    --hover-button-color: var(--button-color);
    --hover-button-background-color: var(--button-background-color);

    --stuck-background-color: var(--background-color);

    @include mq($from: small) {
      --grid-template-areas: var(
        --stzh-cta-small-grid-template-areas,
        "cta cta cta cta"
      );
    }

    @include mq($from: medium) {
      --grid-template-areas: var(
        --stzh-cta-small-grid-template-areas,
        "cta cta cta cta cta cta cta cta"
      );
    }
  }
}

@keyframes stzh-cta-effect {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateX(0);
  }

  40% {
    transform: translateX(-8px);
  }

  60% {
    transform: translateX(-4px);
  }
}

.stzh-cta {
  @include font("heavy");
  @include gridGutter;
  display: grid;
  grid-template-areas: var(--grid-template-areas);
  grid-template-columns: var(--grid-template-columns);

  &__wrapper {
    grid-area: cta;
    background-color: var(--background-color);
    transition-property: background-color;
    transition-duration: $baseTransitionAnimationSpeed;
  }

  &__wrapper:has(#{&}__sticky[is-stuck]:not([is-stuck="false"])) {
    background-color: var(--stuck-background-color);
  }

  &__heading,
  &__lead {
    transition-property: color;
    transition-duration: $baseTransitionAnimationSpeed;

    &:not(:empty) {
      @include spaceCurve("margin-left", "medium");
      @include spaceCurve("margin-right", "medium");
      @include spaceCurve("margin-bottom", "medium");

      @include mq($from: small) {
        margin-bottom: space("xxlarge");
      }

      @include mq($from: medium) {
        margin-bottom: space("xxxlarge");
      }

      @include mq($from: large) {
        margin-bottom: space("xxxxlarge");
      }
    }
  }

  &__heading {
    @include fontCurve("h2", "heading");
    color: var(--heading-color);
  }

  &__wrapper:has(#{&}__sticky[is-stuck]:not([is-stuck="false"])) &__heading {
    color: var(--stuck-heading-color);
  }

  &__lead {
    @include fontCurve("p1");
    color: var(--lead-color);

    &:not(:empty) {
      @include spaceCurve("padding-top", "small");
    }
  }

  &__wrapper:has(#{&}__sticky[is-stuck]:not([is-stuck="false"])) &__lead {
    color: var(--stuck-lead-color);
  }

  &__button {
    @include font("heavy");
    @include fontSize("centi");
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    appearance: none;
    border: none;
    text-decoration: none;
    border-radius: var(--button-border-radius);
    color: var(--button-color);
    background-color: var(--button-background-color);
    gap: space("xsmall");
    transition-property:
      width, height, min-height, color, background-color, font-size, padding;
    transition-duration: $baseTransitionAnimationSpeed;

    @include mq($from: small) {
      width: auto;
    }

    &:hover {
      color: var(--hover-button-color);
      background-color: var(--hover-button-background-color);
    }
  }

  &__sticky[is-stuck]:not([is-stuck="false"]) &__button {
    @include fontSize("milli");

    @include mq($from: medium) {
      @include fontSize("centi");
    }
  }

  &__button-text {
    display: block;
  }

  &__button-icon {
    --size: #{iconSize("medium")};
  }

  &__button:hover &__button-icon {
    animation: stzh-cta-effect 1s;
  }

  &__sticky {
    @include spaceCurve("--padding-left", "medium");
    @include spaceCurve("--padding-right", "medium");
    --padding-top: #{space("large")};
    --padding-bottom: #{space("large")};
    --background-color: var(--sticky-background-color);
    --stuck-background-color: var(--stuck-sticky-background-color);
    --stuck-padding-left: 0px;
    --stuck-padding-right: 0px;

    display: block;
  }

  &__buttons {
    display: flex;
    gap: space("medium");
  }

  /* Has lead variant */

  &--has-lead &__heading {
    margin-bottom: 0;
  }

  /* Default variant; has heading, lead or multiple buttons */

  &--size-default &__wrapper {
    padding-top: space("large");
    padding-bottom: space("large");

    @include mq($from: small) {
      @include spaceCurve("padding-top", "large");
      @include spaceCurve("padding-bottom", "large");
    }
  }

  &--size-default &__button {
    padding: space("xsmall") space("xlarge");
    min-height: $formInputHeight;
  }

  &--size-default &__button-icon {
    @include mq($to: small) {
      display: none;
    }
  }

  &--size-default &__sticky[is-stuck]:not([is-stuck="false"]) &__button {
    min-height: $formInputHeightSmall;

    @include mq($from: medium) {
      min-height: $formInputHeight;
    }
  }

  &--size-default &__sticky {
    margin-top: calc(var(--padding-top) / -1);
    margin-bottom: calc(var(--padding-bottom) / -1);
  }

  /* Small variant; has no heading, lead or multiple buttons */

  &--size-small &__button {
    width: 100%;
    max-width: 100%;
    gap: space("medium");

    &.is-placeholder {
      width: auto;
      max-width: none;
      visibility: hidden;
      position: absolute;
      pointer-events: none;
    }
  }

  &--size-small
    &__sticky[is-stuck]:not([is-stuck="false"])
    &__button:not(.is-placeholder) {
    width: var(--button-width, auto);
  }

  &--size-small &__button-icon {
    --size: #{iconSize("large")};
  }

  &--size-small &__sticky {
    @include spaceCurve("--padding-top", "large");
    @include spaceCurve("--padding-bottom", "large");

    &[is-stuck]:not([is-stuck="false"]) {
      @include spaceCurve("--padding-top", "medium");
      @include spaceCurve("--padding-bottom", "medium");
    }
  }

  /* Not loaded */

  &--is-not-loaded {
    visibility: hidden;
  }

  &--is-not-loaded &__wrapper,
  &--is-not-loaded &__heading,
  &--is-not-loaded &__lead,
  &--is-not-loaded &__button {
    transition-duration: 0ms;
  }
}
